import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { cmdApi, clearToken, saveToken } from "../api";

const CmdAuthContext = createContext(null);

const MODULES = ["portfolio", "board", "decisions", "financials", "reports", "review", "access"];
const PREVIEW_KEY = "cmd_preview";

// Restore any Preview-As headers on page load so the axios instance keeps mimicking the role.
(function restorePreview() {
  try {
    const raw = typeof window !== "undefined" && sessionStorage.getItem(PREVIEW_KEY);
    if (!raw) return;
    const p = JSON.parse(raw);
    if (p?.role) {
      cmdApi.defaults.headers["X-Preview-As"] = p.role;
      cmdApi.defaults.headers["X-Preview-Verticals"] = (p.verticals || []).join(",");
    }
  } catch { /* noop */ }
})();

export function CmdAuthProvider({ children }) {
  const [me, setMe] = useState(null); // null = loading, false = anonymous, object = signed in
  const [setupNeeded, setSetupNeeded] = useState(null); // null = unknown, boolean once resolved

  const refresh = useCallback(async () => {
    try {
      const { data } = await cmdApi.get("/auth/me");
      setMe(data);
      return data;
    } catch {
      setMe(false);
      return null;
    }
  }, []);

  const checkSetup = useCallback(async () => {
    try {
      const { data } = await cmdApi.get("/auth/setup-needed");
      setSetupNeeded(!!data.needed);
      return data.needed;
    } catch {
      setSetupNeeded(false);
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const needed = await checkSetup();
      if (!needed) {
        await refresh();
      } else {
        setMe(false);
      }
    })();
  }, [checkSetup, refresh]);

  const login = async (email, password) => {
    const { data } = await cmdApi.post("/auth/login", { email, password });
    saveToken(data.access_token);
    await refresh();
    return data.user;
  };

  const setupFounder = async (name, email, password) => {
    const { data } = await cmdApi.post("/auth/setup", { name, email, password });
    saveToken(data.access_token);
    setSetupNeeded(false);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await cmdApi.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setMe(false);
  };

  return (
    <CmdAuthContext.Provider
      value={{ me, setupNeeded, login, logout, setupFounder, refresh, modules: MODULES }}
    >
      {children}
    </CmdAuthContext.Provider>
  );
}

export function useCmdAuth() {
  const ctx = useContext(CmdAuthContext);
  if (!ctx) throw new Error("useCmdAuth must be used inside <CmdAuthProvider>");
  return ctx;
}
